import React, { useState, useEffect } from "react";
import api from "../../services/api";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Chip, Tooltip, IconButton
} from "@material-ui/core";
import BackupIcon from "@material-ui/icons/Backup";
import RestoreIcon from "@material-ui/icons/Restore";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import DeleteIcon from "@material-ui/icons/Delete";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { toast } from "react-toastify";

export default function BackupPage() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningNow, setRunningNow] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backup/list");
      setBackups(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await api.post("/backup/run-now");
      toast.success(`Backup gerado: ${data.filename} (${data.sizeMB} MB)`);
      await load();
    } catch (e) {
      toast.error("Erro ao gerar backup");
    }
    setRunningNow(false);
  };

  const handleDownload = (filename) => {
    const token = localStorage.getItem("token");
    const link = document.createElement("a");
    link.href = `/api/backup/download/${filename}`;
    link.setAttribute("download", filename);
    // Add auth header via fetch
    fetch(`/api/backup/download/${filename}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Erro ao baixar backup"));
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      await api.post(`/backup/restore-local/${restoreTarget}`);
      toast.success("Banco restaurado com sucesso! Reinicie o sistema se necessario.");
      setRestoreTarget(null);
    } catch (e) {
      toast.error("Erro ao restaurar backup");
    }
    setRestoring(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/backup/${deleteTarget}`);
      setBackups(prev => prev.filter(b => b.name !== deleteTarget));
      setDeleteTarget(null);
      toast.success("Backup removido");
    } catch (e) { toast.error("Erro ao remover backup"); }
  };

  const fmtDate = (d) => {
    const dt = new Date(d);
    return dt.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const lastBackup = backups[0];
  const lastBackupAge = lastBackup ? Math.round((Date.now() - new Date(lastBackup.createdAt).getTime()) / 3600000) : null;

  return (
    <Box style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      {/* Header */}
      <Box style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <BackupIcon style={{ color: "#01BBAC", fontSize: 28 }} />
        <Typography variant="h5" style={{ fontWeight: 700, color: "#333" }}>Backup do Sistema</Typography>
      </Box>

      {/* Info box */}
      <Paper style={{ padding: 16, marginBottom: 20, background: "rgba(1,187,172,0.06)", border: "1px solid rgba(1,187,172,0.3)", borderRadius: 10 }}>
        <Typography style={{ fontSize: 13, color: "#444", lineHeight: 1.7 }}>
          <strong>Backup automatico:</strong> todos os dias as <strong>03:00h</strong>, o sistema salva um backup completo do banco de dados.<br />
          Sao mantidos os <strong>10 ultimos backups</strong>. Cada backup contem: mensagens, tickets, contatos, kanban, etiquetas, fluxos, usuarios e configuracoes de todos os clientes.<br />
          <strong>Para restaurar:</strong> clique em "Restaurar" no backup desejado. O sistema voltara exatamente ao estado daquele momento.
        </Typography>
      </Paper>

      {/* Status card + botao rodar agora */}
      <Box style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <Paper style={{ flex: 1, padding: 16, borderRadius: 10, border: "1px solid #eee", minWidth: 200 }}>
          <Typography style={{ fontSize: 12, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Ultimo Backup</Typography>
          {lastBackup ? (
            <Box style={{ marginTop: 8 }}>
              <Typography style={{ fontWeight: 700, fontSize: 15, color: "#222" }}>{fmtDate(lastBackup.createdAt)}</Typography>
              <Typography style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{lastBackup.sizeMB} MB</Typography>
              <Chip
                size="small"
                icon={<CheckCircleIcon style={{ color: lastBackupAge < 25 ? "#00875A" : "#e65100" }} />}
                label={lastBackupAge < 25 ? "Recente" : `Ha ${lastBackupAge}h`}
                style={{ marginTop: 6, background: lastBackupAge < 25 ? "rgba(0,135,90,0.1)" : "rgba(230,81,0,0.1)", color: lastBackupAge < 25 ? "#00875A" : "#e65100", fontWeight: 600 }}
              />
            </Box>
          ) : (
            <Typography style={{ color: "#aaa", marginTop: 8 }}>Nenhum backup ainda</Typography>
          )}
        </Paper>

        <Paper style={{ flex: 1, padding: 16, borderRadius: 10, border: "1px solid #eee", minWidth: 200, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Typography style={{ fontSize: 12, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Acao Manual</Typography>
          <Button
            variant="contained"
            startIcon={runningNow ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <PlayArrowIcon />}
            onClick={handleRunNow}
            disabled={runningNow}
            style={{ background: "#01BBAC", color: "#fff", fontWeight: 700, textTransform: "none" }}
          >
            {runningNow ? "Gerando backup..." : "Fazer Backup Agora"}
          </Button>
          <Typography style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>Salva um backup imediatamente (alem do automatico)</Typography>
        </Paper>

        <Paper style={{ flex: 1, padding: 16, borderRadius: 10, border: "1px solid #eee", minWidth: 200, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Typography style={{ fontSize: 12, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Total de Backups</Typography>
          <Typography style={{ fontWeight: 700, fontSize: 28, color: "#01BBAC" }}>{backups.length}</Typography>
          <Typography style={{ fontSize: 11, color: "#aaa" }}>de 10 slots disponiveis</Typography>
          <Typography style={{ fontSize: 11, color: "#aaa" }}>Total: {(backups.reduce((a, b) => a + parseFloat(b.sizeMB), 0)).toFixed(1)} MB em disco</Typography>
        </Paper>
      </Box>

      {/* Lista de backups */}
      <Paper style={{ borderRadius: 10, border: "1px solid #eee", overflow: "hidden" }}>
        <Box style={{ padding: "12px 16px", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography style={{ fontWeight: 700, fontSize: 14 }}>Backups Disponiveis</Typography>
          <Button size="small" onClick={load} style={{ textTransform: "none", color: "#01BBAC" }}>Atualizar</Button>
        </Box>
        {loading ? (
          <Box style={{ padding: 40, display: "flex", justifyContent: "center" }}><CircularProgress style={{ color: "#01BBAC" }} /></Box>
        ) : backups.length === 0 ? (
          <Box style={{ padding: 40, textAlign: "center", color: "#aaa" }}>
            <BackupIcon style={{ fontSize: 48, color: "#ddd", marginBottom: 8 }} />
            <Typography>Nenhum backup encontrado. Clique em "Fazer Backup Agora" para criar o primeiro.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: "#fafafa" }}>
                <TableCell style={{ fontWeight: 700, fontSize: 12 }}>Data e Hora</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 12 }}>Arquivo</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 12 }} align="right">Tamanho</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 12 }} align="center">Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.map((bk, i) => (
                <TableRow key={bk.name} style={{ background: i === 0 ? "rgba(1,187,172,0.04)" : "inherit" }}>
                  <TableCell>
                    <Box style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Typography style={{ fontSize: 13, fontWeight: i === 0 ? 700 : 400 }}>{fmtDate(bk.createdAt)}</Typography>
                      {i === 0 && <Chip size="small" label="Mais recente" style={{ fontSize: 10, height: 18, background: "rgba(1,187,172,0.15)", color: "#01BBAC" }} />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography style={{ fontSize: 11, color: "#888", fontFamily: "monospace" }}>{bk.name}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography style={{ fontSize: 13, fontWeight: 600 }}>{bk.sizeMB} MB</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                      <Tooltip title="Baixar backup">
                        <IconButton size="small" onClick={() => handleDownload(bk.name)}>
                          <CloudDownloadIcon fontSize="small" style={{ color: "#1565c0" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Restaurar banco para este backup">
                        <IconButton size="small" onClick={() => setRestoreTarget(bk.name)}>
                          <RestoreIcon fontSize="small" style={{ color: "#e65100" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir backup">
                        <IconButton size="small" onClick={() => setDeleteTarget(bk.name)}>
                          <DeleteIcon fontSize="small" style={{ color: "#e57373" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Dialog restaurar */}
      <Dialog open={!!restoreTarget} onClose={() => !restoring && setRestoreTarget(null)}>
        <DialogTitle style={{ color: "#e65100" }}>Restaurar Backup</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>ATENCAO:</strong> Restaurar o backup substituira TODOS os dados atuais do banco pelo estado do backup selecionado.<br /><br />
            Backup: <code style={{ background: "#f5f5f5", padding: "2px 6px", borderRadius: 4 }}>{restoreTarget}</code><br /><br />
            Esta acao nao pode ser desfeita. Certifique-se de que deseja voltar para este ponto.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)} disabled={restoring}>Cancelar</Button>
          <Button
            onClick={handleRestore}
            color="secondary"
            variant="contained"
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <RestoreIcon />}
          >
            {restoring ? "Restaurando..." : "Confirmar Restauracao"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog excluir */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Excluir Backup</DialogTitle>
        <DialogContent>
          <DialogContentText>Deseja excluir o backup <strong>{deleteTarget}</strong>? Esta acao nao pode ser desfeita.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
